/**
 * Soccer ball image cursor trail for pointer devices.
 * Disabled on touch, reduced-motion, and theme editor.
 */
(function initCursorTrail() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isFinePointer = window.matchMedia('(any-pointer: fine)').matches;
  const isDesignMode = document.documentElement.classList.contains('shopify-design-mode');
  const script = document.currentScript;
  const ballSrc = script instanceof HTMLScriptElement ? script.dataset.ballSrc : '';

  if (!ballSrc || !isFinePointer || prefersReducedMotion || isDesignMode) return;

  const ballImage = new Image();
  ballImage.decoding = 'async';
  ballImage.src = ballSrc;

  ballImage.onload = () => {
    startTrail(prepareBallSprite(ballImage));
  };

  /**
   * Removes the black background so the ball blends on any page color.
   * @param {HTMLImageElement} sourceImage
   * @returns {HTMLCanvasElement}
   */
  function prepareBallSprite(sourceImage) {
    const sprite = document.createElement('canvas');
    sprite.width = sourceImage.naturalWidth;
    sprite.height = sourceImage.naturalHeight;

    const spriteCtx = sprite.getContext('2d');
    if (!spriteCtx) return sprite;

    spriteCtx.drawImage(sourceImage, 0, 0);
    const imageData = spriteCtx.getImageData(0, 0, sprite.width, sprite.height);
    const { data } = imageData;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r < 40 && g < 40 && b < 40) {
        data[i + 3] = 0;
      }
    }

    spriteCtx.putImageData(imageData, 0, 0);
    return sprite;
  }

  /**
   * @param {HTMLCanvasElement} ballSprite
   */
  function startTrail(ballSprite) {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999;';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    /** @type {{ x: number; y: number; life: number; size: number; rotation: number; spin: number }[]} */
    const particles = [];
    const maxParticles = 16;
    let rafId = 0;
    let lastSpawnX = -100;
    let lastSpawnY = -100;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    /**
     * @param {number} x
     * @param {number} y
     */
    function spawnParticle(x, y) {
      const dx = x - lastSpawnX;
      const dy = y - lastSpawnY;
      if (dx * dx + dy * dy < 16) return;

      lastSpawnX = x;
      lastSpawnY = y;

      if (particles.length >= maxParticles) particles.shift();

      particles.push({
        x,
        y,
        life: 1,
        size: 13 + Math.random() * 6,
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.14,
      });
    }

    function draw() {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.life -= 0.04;
        p.size *= 0.98;
        p.rotation += p.spin;

        if (p.life <= 0 || p.size < 5) {
          particles.splice(i, 1);
          continue;
        }

        const drawSize = p.size * 1.75;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = p.life * 0.9;
        ctx.drawImage(ballSprite, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
        ctx.restore();
      }

      ctx.globalAlpha = 1;
      rafId = window.requestAnimationFrame(draw);
    }

    /** @param {PointerEvent} event */
    function onPointerMove(event) {
      spawnParticle(event.clientX, event.clientY);
    }

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    rafId = window.requestAnimationFrame(draw);

    window.addEventListener(
      'pagehide',
      () => {
        window.cancelAnimationFrame(rafId);
        window.removeEventListener('resize', resize);
        window.removeEventListener('pointermove', onPointerMove);
        canvas.remove();
      },
      { once: true }
    );
  }
})();
